import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

// Design System Constants (matching other components)
const DESIGN_SYSTEM = {
  buttons: {
    primary: 'bg-gradient-to-r from-ripple-blue to-ripple-blue-light hover:from-ripple-blue/90 hover:to-ripple-blue-light/90 text-white font-semibold rounded-lg transition-all duration-300 shadow-lg shadow-ripple-blue/25 border border-ripple-blue/30',
    secondary: 'bg-white/5 hover:bg-white/10 backdrop-blur-md border border-white/20 hover:border-white/30 text-white font-semibold rounded-lg transition-all duration-300'
  }
};

const RippleOneLogo = () => (
  <div className="relative group">
    <svg width="28" height="28" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg" className="drop-shadow-lg">
      <defs>
        <linearGradient id="rippleGradientHeader" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" style={{stopColor:"#4FC3F7", stopOpacity:1}} />
          <stop offset="50%" style={{stopColor:"#2196F3", stopOpacity:1}} />
          <stop offset="100%" style={{stopColor:"#1976D2", stopOpacity:1}} />
        </linearGradient>
      </defs>
      
      {/* Water Ripple Icon */}
      <g transform="translate(4, 4)">
        {/* Outer ripple */}
        <circle cx="12" cy="12" r="10" fill="none" stroke="url(#rippleGradientHeader)" strokeWidth="1.2" opacity="0.3"/>
        {/* Middle ripple */}
        <circle cx="12" cy="12" r="6.5" fill="none" stroke="url(#rippleGradientHeader)" strokeWidth="1.2" opacity="0.6"/>
        {/* Inner ripple */}
        <circle cx="12" cy="12" r="3.5" fill="none" stroke="url(#rippleGradientHeader)" strokeWidth="1.2" opacity="0.9"/>
        {/* Center drop */}
        <circle cx="12" cy="12" r="1.2" fill="url(#rippleGradientHeader)"/>
      </g>
    </svg>
  </div>
);

const Header: React.FC = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolling, setIsScrolling] = useState(false);
  const [hideHeader, setHideHeader] = useState(false);
  const [lastScrollY, setLastScrollY] = useState(0);

  useEffect(() => {
    let scrollTimeout: NodeJS.Timeout;

    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      
      // Set scrolled state
      setIsScrolled(currentScrollY > 20);
      
      // Set scrolling state
      setIsScrolling(true);
      
      // Clear existing timeout
      clearTimeout(scrollTimeout);
      
      // Hide header when scrolling down, show when scrolling up or near top
      if (currentScrollY > lastScrollY && currentScrollY > 100) {
        // Scrolling down
        setHideHeader(true);
      } else if (currentScrollY < lastScrollY || currentScrollY < 50) {
        // Scrolling up or near top
        setHideHeader(false);
      }
      
      setLastScrollY(currentScrollY);
      
      // Stop scrolling after delay
      scrollTimeout = setTimeout(() => {
        setIsScrolling(false);
        // Show header when scrolling stops
        setHideHeader(false);
      }, 150);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', handleScroll);
      clearTimeout(scrollTimeout);
    };
  }, [lastScrollY]);

  const handleLaunchApp = () => {
    window.location.href = '/app';
  };

  const navItems = [
    { name: 'How It Works', href: '#how-it-works' },
    { name: 'Performance', href: '#stats' },
    { name: 'Partners', href: '#partners' }
  ];

  return (
    <motion.header 
      className="fixed top-0 left-0 right-0 z-50 transition-all duration-300"
      style={{
        // Dark background when still, slightly transparent when scrolled
        backgroundColor: isScrolled ? 'rgba(15, 23, 42, 0.95)' : 'rgba(15, 23, 42, 0.98)',
        backdropFilter: 'blur(16px)',
        borderBottom: isScrolled ? '1px solid rgba(148, 163, 184, 0.1)' : '1px solid rgba(148, 163, 184, 0.05)',
        boxShadow: isScrolled ? '0 4px 20px rgba(0, 0, 0, 0.1)' : '0 2px 10px rgba(0, 0, 0, 0.05)',
      }}
      initial={{ y: -100 }}
      animate={{ 
        y: hideHeader && isScrolling ? -100 : 0,
        opacity: hideHeader && isScrolling ? 0 : 1
      }}
      transition={{ 
        duration: hideHeader ? 0.3 : 0.6,
        ease: "easeInOut"
      }}
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 xl:px-16">
        <div className="flex items-center justify-between h-14">
          
          {/* Logo Section */}
          <div className="flex items-center space-x-2">
            <RippleOneLogo />
            <span className="text-lg font-bold tracking-tight text-white">
              RippleOne
            </span>
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center space-x-6">
            {navItems.map((item) => (
              <a
                key={item.name}
                href={item.href}
                className="text-sm font-medium text-slate-300 hover:text-white transition-colors duration-200 relative group py-2"
                onClick={(e) => {
                  e.preventDefault();
                  const element = document.querySelector(item.href);
                  element?.scrollIntoView({ behavior: 'smooth' });
                }}
              >
                {item.name}
                <div className="absolute -bottom-0 left-0 h-0.5 bg-gradient-to-r from-ripple-blue to-accent-blue rounded-full w-0 group-hover:w-full transition-all duration-300" />
              </a>
            ))}
          </nav>

          {/* Action Buttons */}
          <div className="flex items-center space-x-3">
            {/* Compact Desktop CTA Button */}
            <button
              onClick={handleLaunchApp}
              className={`hidden sm:inline-flex ${DESIGN_SYSTEM.buttons.primary} px-5 py-2 text-sm font-medium justify-center items-center`}
            >
              Launch App
            </button>

            {/* Mobile Menu Button */}
            <button
              className="lg:hidden w-8 h-8 rounded-lg bg-slate-700/50 backdrop-blur-md border border-slate-600/30 flex items-center justify-center text-white hover:bg-slate-600/50 transition-all duration-200"
              onClick={() => setIsMenuOpen(!isMenuOpen)}
            >
              <div className="w-4 h-4 relative">
                <span
                  className={`absolute top-0 left-0 w-full h-0.5 bg-white rounded-full transition-all duration-300 ${
                    isMenuOpen ? 'rotate-45 translate-y-1.5' : 'rotate-0 translate-y-0'
                  }`}
                />
                <span
                  className={`absolute top-1.5 left-0 w-full h-0.5 bg-white rounded-full transition-all duration-300 ${
                    isMenuOpen ? 'opacity-0' : 'opacity-100'
                  }`}
                />
                <span
                  className={`absolute top-3 left-0 w-full h-0.5 bg-white rounded-full transition-all duration-300 ${
                    isMenuOpen ? '-rotate-45 -translate-y-1.5' : 'rotate-0 translate-y-0'
                  }`}
                />
              </div>
            </button>
          </div>
        </div>

        {/* Mobile Navigation Menu */}
        {isMenuOpen && (
          <div className="lg:hidden absolute top-full left-0 right-0 bg-slate-900/95 backdrop-blur-xl border-t border-slate-700/30">
            <div className="px-6 py-4 space-y-4">
              {/* Mobile Navigation Links */}
              {navItems.map((item) => (
                <a
                  key={item.name}
                  href={item.href}
                  className="block text-base font-medium text-slate-300 hover:text-white transition-colors duration-200 py-1"
                  onClick={(e) => {
                    e.preventDefault();
                    const element = document.querySelector(item.href);
                    element?.scrollIntoView({ behavior: 'smooth' });
                    setIsMenuOpen(false);
                  }}
                >
                  {item.name}
                </a>
              ))}
              
              {/* Mobile CTA Button */}
              <button
                onClick={() => {
                  handleLaunchApp();
                  setIsMenuOpen(false);
                }}
                className={`w-full ${DESIGN_SYSTEM.buttons.primary} px-5 py-2.5 text-sm font-medium mt-2 flex justify-center items-center`}
              >
                Launch App
              </button>
            </div>
          </div>
        )}
      </div>
    </motion.header>
  );
};

export default Header; 